import React from 'react';
import API_BASE_URL from '../../config';

// What visitors asked that the organization's published content doesn't
// answer, grouped into topics (app/analytics/content_gaps.py). The fix for a
// topic here is the organization adding the answer to its website, which
// improves the site and the assistant at once.
//
// Tenants read /api/v1/analytics/content-gaps with their customer key; the
// ops console passes `operator` and reads /api/admin/ops/content-gaps, which
// adds each topic's gates, best search score and request ids.

const cell = { padding: '8px 10px', borderBottom: '1px solid #eee', textAlign: 'left', fontSize: 13, verticalAlign: 'top' };
const muted = { color: '#666', fontSize: 12 };
const day = (ts) => new Date(ts * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

export const PAGE_HOW = {
  routed: 'Assistant pointed visitors to',
  cited: 'Partly answered from',
  closest: 'Closest page found',
};

function dates(t) {
  const first = day(t.first_asked);
  const last = day(t.last_asked);
  return first === last ? first : `${first} – ${last}`;
}

function outcomes(kinds = {}) {
  return [
    kinds.not_found && `${kinds.not_found} not answered`,
    kinds.uncited && `${kinds.uncited} answered without a source`,
    kinds.partial && `${kinds.partial} partly answered`,
  ].filter(Boolean);
}

function TopicRow({ topic, operator }) {
  return <tr>
    <td style={{ ...cell, minWidth: 260 }}>
      <div style={{ fontWeight: 600 }}>{topic.label}</div>
      {topic.examples?.length > 0 && <div style={muted}>Also asked as: {topic.examples.join(' · ')}</div>}
      {topic.missing?.map((m) => <div key={m} style={{ ...muted, fontStyle: 'italic', marginTop: 2 }}>“{m}”</div>)}
    </td>
    <td style={{ ...cell, minWidth: 130 }}>
      <div style={{ whiteSpace: 'nowrap' }}>
        {topic.conversations} {topic.conversations === 1 ? 'conversation' : 'conversations'}
      </div>
      {outcomes(topic.kinds).map((o) => <div key={o} style={muted}>{o}</div>)}
    </td>
    <td style={{ ...cell, minWidth: 160 }}>
      {topic.page ? <>
        <div style={muted}>{PAGE_HOW[topic.page.how] || 'Page'}</div>
        {topic.page.url ? <a href={topic.page.url} target="_blank" rel="noopener noreferrer">{topic.page.title}</a>
          : topic.page.title}
      </> : <span style={muted}>—</span>}
    </td>
    <td style={{ ...cell, whiteSpace: 'nowrap' }}>{dates(topic)}</td>
    {operator && <td style={{ ...cell, fontSize: 12 }}>
      {Object.entries(topic.operator?.gates || {}).map(([g, n]) => `${g} ${n}`).join(', ')}
      {topic.operator?.max_score != null && <div>best score {topic.operator.max_score}</div>}
      {(topic.operator?.request_ids || []).length > 0 && <details style={muted}>
        <summary style={{ cursor: 'pointer' }}>request ids</summary>
        <div style={{ wordBreak: 'break-all' }}>{topic.operator.request_ids.join(' ')}</div>
      </details>}
    </td>}
  </tr>;
}

function TopicTable({ topics, operator, label }) {
  return <div style={{ overflowX: 'auto' }}>
    <table aria-label={label} style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead><tr>
        {['Topic', 'Asked', 'Page', 'When', ...(operator ? ['Detail'] : [])].map((h) => <th key={h} style={cell}>{h}</th>)}
      </tr></thead>
      <tbody>{topics.map((t) => <TopicRow key={`${t.label}-${t.first_asked}`} topic={t} operator={operator} />)}</tbody>
    </table>
  </div>;
}

/** The content-gaps report for one corpus. `days` comes from the analytics
 *  period when the parent has one; without it the panel has its own. */
export default function ContentGaps({ corpusId, days: parentDays, customerKey, adminKey, operator = false }) {
  const [ownDays, setOwnDays] = React.useState(30);
  const [partial, setPartial] = React.useState(true);
  const [state, setState] = React.useState({ loading: true });
  const days = parentDays || ownDays;

  React.useEffect(() => {
    let alive = true;
    setState({ loading: true });
    const params = new URLSearchParams({ corpus_id: corpusId, days, partial: partial ? '1' : '0' });
    const path = operator ? '/api/admin/ops/content-gaps' : '/api/v1/analytics/content-gaps';
    const headers = adminKey ? { 'X-Admin-Key': adminKey } : { 'X-Customer-Key': customerKey };
    fetch(`${API_BASE_URL}${path}?${params}`, { headers })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((body) => { if (alive) setState({ body }); })
      .catch((e) => { if (alive) setState({ error: `Could not load unanswered questions: ${e.message}` }); });
    return () => { alive = false; };
  }, [corpusId, days, partial, customerKey, adminKey, operator]);

  const body = state.body || {};
  const topics = body.topics || [];
  const offTopic = body.off_topic || [];
  return <section aria-label="Unanswered questions" style={{ margin: '16px 0' }}>
    <h3 style={{ fontSize: 16, margin: '0 0 4px' }}>Questions your website doesn’t answer</h3>
    <p style={{ ...muted, margin: '0 0 8px' }}>
      What visitors asked that your published pages don’t cover, grouped by topic and ranked by how many
      conversations ran into it. Adding the answer to your website helps visitors and the assistant alike.
    </p>
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8, fontSize: 13 }}>
      {!parentDays && <label>Period <select value={ownDays} onChange={(e) => setOwnDays(Number(e.target.value))}>
        <option value={7}>Last 7 days</option><option value={30}>Last 30 days</option><option value={90}>Last 90 days</option>
      </select></label>}
      <label><input type="checkbox" checked={partial} onChange={(e) => setPartial(e.target.checked)} />
        {' '}Include partly answered questions</label>
    </div>
    {state.loading && <p role="status">Loading unanswered questions…</p>}
    {state.error && <p role="alert">{state.error}</p>}
    {state.body && <>
      {body.truncated && body.covered_from && <p style={muted}>Covers the newest questions, back to {body.covered_from}.</p>}
      {body.grouping === 'wording' && <p style={muted}>Grouped by exact wording only (topic grouping is unavailable right now).</p>}
      {topics.length === 0 ? <p>No unanswered questions in this period.</p>
        : <TopicTable topics={topics} operator={operator} label="Unanswered topics" />}
      {offTopic.length > 0 && <details style={{ marginTop: 10 }}>
        <summary style={{ cursor: 'pointer', fontSize: 13 }}>
          {body.totals?.off_topic || offTopic.length} judged not about your organization (left out above)
        </summary>
        <TopicTable topics={offTopic} operator={operator} label="Not about your organization" />
      </details>}
    </>}
  </section>;
}
